import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import * as Sentry from '@sentry/react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from './ConfirmDialog';
import { isDataResetToolsEnabled } from '@/lib/build-info';
import { buildDiagnosticsEmailPayload } from '@/lib/diagnosticsReport';
import { errorLogger } from '@/lib/errorLogger';

type DataLoadIssue = {
  summary: string;
  canRecover: boolean;
};

const FALLBACK_ERROR_MESSAGE =
  'Si è verificato un errore imprevisto. Riprova o contatta l’assistenza.';

const resolveZodIssues = (error: Error): unknown[] | null => {
  const directIssues = (error as { issues?: unknown[] }).issues;
  if (Array.isArray(directIssues)) {
    return directIssues;
  }

  const message = error.message?.trim();
  if (!message || !message.startsWith('[')) return null;

  try {
    const parsed = JSON.parse(message);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const resolveDataLoadIssue = (error: unknown): DataLoadIssue | null => {
  if (!(error instanceof Error)) return null;

  const issues = resolveZodIssues(error);
  if (!issues?.length) return null;

  const hasInvalidType = issues.some((issue) => {
    if (!issue || typeof issue !== 'object') return false;
    return (issue as { code?: string }).code === 'invalid_type';
  });

  if (!hasInvalidType) return null;

  const canRecover = isDataResetToolsEnabled();
  return {
    summary: canRecover
      ? 'Alcuni dati salvati non sono più compatibili con questa versione. Puoi rigenerare i dati di esempio o eliminare tutti i dati per continuare. Queste opzioni sono disponibili anche in Impostazioni → Dati. È una misura temporanea per accelerare lo sviluppo finché la struttura dati non è stabilizzata.'
      : 'Alcuni dati salvati non sono più compatibili con questa versione. Contatta il supporto per ripristinare i dati.',
    canRecover,
  };
};

type DataRecoveryAlertProps = {
  title: string;
  error: unknown;
  onSeedReset?: () => void | Promise<void>;
  onClearAllData?: () => void | Promise<void>;
  isSeeding?: boolean;
  isClearing?: boolean;
};

export function DataRecoveryAlert({
  title,
  error,
  onSeedReset,
  onClearAllData,
  isSeeding = false,
  isClearing = false,
}: DataRecoveryAlertProps): JSX.Element {
  const [showSeedConfirm, setShowSeedConfirm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [userDescription, setUserDescription] = useState('');

  // Log error and capture in Sentry
  useEffect(() => {
    if (error instanceof Error) {
      errorLogger.logError(error, undefined, {
        category: 'database',
        metadata: { title },
      });
      Sentry.captureException(error, {
        tags: { component: 'DataRecoveryAlert' },
      });
    }
  }, [error, title]);

  const issue = useMemo(() => resolveDataLoadIssue(error), [error]);
  const displayMessage =
    issue?.summary || (error instanceof Error ? error.message : null) || FALLBACK_ERROR_MESSAGE;

  const handleSendReport = () => {
    const errorMessage = error instanceof Error ? error.message : String(error);

    const payload = buildDiagnosticsEmailPayload(userDescription, {
      errorId: `data-error-${Date.now()}`,
      message: errorMessage,
      url: window.location.href,
    });

    const mailtoUrl = `mailto:?subject=${encodeURIComponent(payload.subject)}&body=${encodeURIComponent(payload.body)}`;
    window.location.href = mailtoUrl;
  };

  const canRecover = Boolean(issue?.canRecover && onSeedReset && onClearAllData);

  const handleSeedReset = async () => {
    if (!onSeedReset) return;
    await onSeedReset();
  };

  const handleClearAllData = async () => {
    if (!onClearAllData) return;
    await onClearAllData();
  };

  return (
    <>
      <Alert variant="destructive">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>
          <div className="space-y-3">
            <p className="text-sm text-text-primary">{displayMessage}</p>
            {canRecover ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => setShowSeedConfirm(true)}
                  disabled={isSeeding || isClearing}
                >
                  Rigenera dati di esempio
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowClearConfirm(true)}
                  disabled={isSeeding || isClearing}
                >
                  Elimina tutti i dati
                </Button>
              </div>
            ) : null}

            {/* Error reporting section */}
            <div className="pt-2 border-t border-danger-500/20">
              {!showReportForm ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowReportForm(true)}
                  className="text-danger-500 hover:text-danger-500/80"
                >
                  Segnala problema
                </Button>
              ) : (
                <div className="space-y-2">
                  <label
                    htmlFor="error-description"
                    className="text-xs font-medium text-danger-500/80"
                  >
                    Cosa stavi facendo quando è apparso l&apos;errore?
                  </label>
                  <Textarea
                    id="error-description"
                    value={userDescription}
                    onChange={(e) => setUserDescription(e.target.value)}
                    placeholder="Es: Ho aperto la lista aziende dopo aver importato un bundle..."
                    className="text-sm bg-white border-danger-500/30 focus:border-danger-500"
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSendReport}>
                      Invia segnalazione
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowReportForm(false)}>
                      Annulla
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </AlertDescription>
      </Alert>

      {canRecover ? (
        <>
          <ConfirmDialog
            open={showSeedConfirm}
            onOpenChange={setShowSeedConfirm}
            title="Rigenerare i dati di esempio?"
            message="Questa azione elimina i dati correnti e ricrea solo dati di esempio. È una funzione temporanea per accelerare lo sviluppo finché la struttura dati non è stabilizzata."
            confirmLabel="Rigenera dati"
            variant="warning"
            isLoading={isSeeding}
            onConfirm={handleSeedReset}
          />

          <ConfirmDialog
            open={showClearConfirm}
            onOpenChange={setShowClearConfirm}
            title="Eliminare tutti i dati?"
            message="Questa azione rimuove aziende, stabilimenti, impianti, report e impostazioni locali. È una funzione temporanea per accelerare lo sviluppo finché la struttura dati non è stabilizzata."
            confirmLabel="Elimina tutti i dati"
            variant="danger"
            isLoading={isClearing}
            onConfirm={handleClearAllData}
          />
        </>
      ) : null}
    </>
  );
}
